import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { loadAdSense } from "@/lib/adsense";
import { useConsent, adsEnabled } from "@/lib/adConsent";
import api from "@/lib/api";

const CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;

export const adsConfigured = () => !!CLIENT;

/**
 * A single AdSense ad unit.
 * - Before AdSense IDs are configured (preview / pre-approval): shows a labeled
 *   "Advertisement" placeholder so the placement is visible.
 * - Once REACT_APP_ADSENSE_CLIENT + a slot are set AND the visitor has consented:
 *   renders a real <ins class="adsbygoogle"> and pushes it once.
 * - `placement` (optional): our own placement key (in_list, side_rail, …). When set,
 *   fires a one-per-mount "times shown" beacon for the admin Ad-tab counter.
 */
export default function AdSlot({ slot, format = "auto", responsive = true, className = "", style, refreshKey = "initial", minHeight = 100, placement = "" }) {
    const consent = useConsent();
    const ok = adsEnabled(consent);
    const ref = useRef(null);
    const lastPathRef = useRef("");
    const { pathname } = useLocation();

    useEffect(() => {
        if (!CLIENT || !slot || !ok || !ref.current) return;
        let cancelled = false;
        loadAdSense(CLIENT)
            .then(() => {
                if (cancelled || !ref.current) return;
                if (ref.current.dataset.adsensePushed === "true") return;
                (window.adsbygoogle = window.adsbygoogle || []).push({});
                ref.current.dataset.adsensePushed = "true";
            })
            .catch((e) => console.error("AdSense", e));
        return () => { cancelled = true; };
    }, [slot, ok, refreshKey]);

    // "Times shown" beacon — once per page view when the slot is actually displayed
    // to the visitor (placeholder in preview, or the real ad once consented). Keyed on
    // pathname so persistent slots (side rails) re-count on each navigation too.
    useEffect(() => {
        if (!placement) return;
        const shown = (!CLIENT || !slot) ? true : ok;
        if (!shown) return;
        if (lastPathRef.current === pathname) return;
        lastPathRef.current = pathname;
        api.post("/analytics/track", { type: "ad_view", slot: placement }).catch(() => {});
    }, [placement, slot, ok, pathname]);

    // Placeholder when AdSense isn't wired up yet — lets you see where ads will sit.
    if (!CLIENT || !slot) {
        return (
            <div className={`ad-wrap ${className}`} data-testid="ad-placeholder" style={{ minHeight, ...style }}>
                <div className="flex h-full w-full items-center justify-center border border-dashed border-[#2a2a2a] bg-[#0f0f0f] text-[10px] uppercase tracking-[0.3em] text-[#4a4a4a]" style={{ minHeight }}>
                    Advertisement
                </div>
            </div>
        );
    }

    // Configured but awaiting the visitor's consent choice.
    if (!ok) return null;

    return (
        <div className={`ad-wrap ${className}`} aria-label="Advertisement" style={{ minHeight, ...style }} data-testid="ad-slot">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#4a4a4a] mb-1 text-center">Advertisement</div>
            <ins
                ref={ref}
                key={`${slot}-${refreshKey}`}
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client={CLIENT}
                data-ad-slot={slot}
                data-ad-format={format}
                data-full-width-responsive={responsive ? "true" : "false"}
            />
        </div>
    );
}
